import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Music, Heart, Users, MessageSquare, Brain, ExternalLink, Sparkles, Check, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { KpiCard, Badge, ETYPE_META, timeAgo } from "./engShared";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function EngOverview() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [genBusy, setGenBusy] = useState(false);
  const { user } = useAuth() || {};
  useEffect(() => {
    api.get("/engagement/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/engagement/insights").then((r) => setInsights(r.data.items || [])).catch(() => {});
  }, []);
  const genInsights = async () => {
    setGenBusy(true);
    try {
      const r = await api.post("/engagement/insights/generate");
      setInsights((prev) => [...(r.data.items || []), ...prev]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Insight generation failed"); }
    setGenBusy(false);
  };
  const insightAct = async (id, action) => {
    try {
      const r = await api.post(`/engagement/insights/${id}/action`, { action });
      if (action === "approve" && r.data.poll_id) toast.success("Poll created and live on your listener page!");
      setInsights((prev) => action === "dismiss"
        ? prev.filter((i) => i.insight_id !== id)
        : prev.map((i) => (i.insight_id === id ? { ...i, status: "approved" } : i)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  if (!data) return <div className="p-8 text-sm text-ink-muted">Loading…</div>;
  const k = data.kpis || {};
  const maxTrend = Math.max(1, ...(data.trend || []).map((t) => t.count));

  return (
    <div className="space-y-5" data-testid="eng-overview-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Engagement Overview</h1>
          <p className="text-sm text-ink-secondary">Your listeners' requests, messages and dedications in one place.</p>
        </div>
        {user?.station_id && (
          <a href={`/engage/${user.station_id}`} target="_blank" rel="noopener noreferrer" data-testid="eng-public-page-link"
            className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-xs font-bold text-ink-secondary hover:text-ink-primary">
            <ExternalLink className="h-3.5 w-3.5" /> Open public listener page
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiCard label="Requests Today" value={k.requests_today ?? 0} accent="text-brand-cyan" testid="eng-kpi-requests" />
        <KpiCard label="Messages Today" value={k.messages_today ?? 0} testid="eng-kpi-messages" />
        <KpiCard label="Dedications Today" value={k.dedications_today ?? 0} accent="text-pink-300" testid="eng-kpi-dedications" />
        <KpiCard label="Unanswered" value={k.unanswered ?? 0} accent={k.unanswered ? "text-brand-amber" : ""} testid="eng-kpi-unanswered" />
        <KpiCard label="New Listeners (7d)" value={k.new_listeners_7d ?? 0} accent="text-emerald-300" testid="eng-kpi-listeners" />
        <KpiCard label="Poll Votes" value={k.poll_votes ?? 0} testid="eng-kpi-polls" />
        <KpiCard label="Contest Entries" value={k.contest_entries ?? 0} testid="eng-kpi-contests" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="eng-top-songs">
          <div className="flex items-center gap-2 text-sm font-bold mb-3"><Music className="h-4 w-4 text-brand-cyan" /> Most Requested (7 days)</div>
          {!data.top_songs?.length && <div className="text-xs text-ink-muted">No requests yet — share your public listener page.</div>}
          {(data.top_songs || []).map((s, i) => (
            <div key={i} className="flex items-center justify-between border-b border-line/50 py-1.5 last:border-0">
              <div className="min-w-0">
                <div className="text-xs font-semibold truncate">{s.song}</div>
                <div className="text-[11px] text-ink-muted truncate">{s.artist}</div>
              </div>
              <span className="text-xs font-mono text-brand-cyan shrink-0 ml-2">{s.count}×</span>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="eng-unanswered-list">
          <div className="flex items-center gap-2 text-sm font-bold mb-3"><Inbox className="h-4 w-4 text-brand-amber" /> Needs a Reply</div>
          {!data.unanswered?.length && <div className="text-xs text-ink-muted">Inbox zero — nice work.</div>}
          {(data.unanswered || []).map((m) => (
            <Link to="/dashboard/engagement/inbox" key={m.message_id} className="block border-b border-line/50 py-1.5 last:border-0 hover:bg-bg-elevated/40 rounded px-1 -mx-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold truncate">{m.listener_name}</span>
                <Badge meta={ETYPE_META[m.etype]} />
              </div>
              <div className="text-[11px] text-ink-muted truncate">{m.message}</div>
            </Link>
          ))}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="eng-trend">
            <div className="flex items-center gap-2 text-sm font-bold mb-3"><MessageSquare className="h-4 w-4 text-emerald-300" /> Engagement (14 days)</div>
            <div className="flex items-end gap-1 h-20">
              {(data.trend?.length ? data.trend : [{ date: "", count: 0 }]).map((t, i) => (
                <div key={i} title={`${t.date}: ${t.count}`} className="flex-1 rounded-t bg-brand-cyan/60 min-w-[4px]"
                  style={{ height: `${Math.max(6, (t.count / maxTrend) * 100)}%` }} />
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="eng-top-listeners">
            <div className="flex items-center gap-2 text-sm font-bold mb-3"><Users className="h-4 w-4 text-violet-300" /> Top Listeners</div>
            {!data.top_listeners?.length && <div className="text-xs text-ink-muted">Profiles build up as listeners interact.</div>}
            {(data.top_listeners || []).map((l) => (
              <div key={l.listener_id} className="flex items-center justify-between py-1 text-xs">
                <span className="truncate">{l.name || l.email}</span>
                <span className="font-mono text-ink-muted">{l.counts?.total || 0} interactions</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="eng-insights-feed">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2 text-sm font-bold"><Brain className="h-4 w-4 text-brand-amber" /> AI Audience Insights</div>
          <button onClick={genInsights} disabled={genBusy} data-testid="eng-insights-generate-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-brand-amber/50 text-brand-amber px-3 py-1.5 text-xs font-bold disabled:opacity-50">
            {genBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {genBusy ? "Analyzing your audience…" : "Generate insights"}
          </button>
        </div>
        {!insights.length && <p className="text-xs text-ink-muted">Hit "Generate insights" and the AI reads your last two weeks of engagement — trends, gaps, and one-tap suggested actions you approve.</p>}
        {insights.map((i) => (
          <div key={i.insight_id} className="flex items-start gap-3 border-b border-line/50 py-2.5 last:border-0" data-testid={`eng-insight-${i.insight_id}`}>
            <Sparkles className="h-3.5 w-3.5 text-brand-amber shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-ink-primary">{i.text}</div>
              {i.action?.type === "create_poll" && i.status !== "approved" && (
                <div className="text-[11px] text-brand-cyan mt-0.5">Suggested: launch poll — "{i.action.question}"</div>
              )}
              {i.status === "approved" && <div className="text-[10px] text-emerald-300 font-mono mt-0.5">APPROVED</div>}
            </div>
            {i.status !== "approved" && (
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => insightAct(i.insight_id, "approve")} data-testid={`eng-insight-approve-${i.insight_id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-500/50 text-emerald-300 px-2.5 py-1 text-[10px] font-bold"><Check className="h-3 w-3" /> {i.action?.type === "create_poll" ? "Launch poll" : "Approve"}</button>
                <button onClick={() => insightAct(i.insight_id, "dismiss")} data-testid={`eng-insight-dismiss-${i.insight_id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-line text-ink-muted px-2.5 py-1 text-[10px]"><X className="h-3 w-3" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
